import React, { useState } from "react";
import { FormattedMessage, FormattedHTMLMessage, useIntl } from "react-intl";

import Seo from "../../../common/helpers/seo";
import { solfaNameOptions, intervalOptions, lengthenSolfa } from "../../../common/parameters";
import makePatternOptions from "../../../common/helpers/makePatternOptions";

import { useUser } from "../../store/user/selectors";
import { Select, SelectCheckbox, CheckboxThreeWay } from "../../components/Form";

import { StyledArticle, P1, P2, H2, H4 } from "../styles";

import { StyledSpring, Flex, Column, Desktop, Mobile } from "./styles";
import messages from "./messages";
import { ISelectEvent } from "../../types";

const Pitch = () => {
    const { formatMessage, locale } = useIntl();
    const { solfaShort = false, solfaKam = false } = useUser();
    const [tonesetValues, setTonesetValues] = useState(["d", "r", "m", "s"]);
    const change = (e: { target: { value: string | undefined}}) => {
        let value = e.target.value;
        if (value === undefined) {
            setTonesetValues([]);
        } else {
            let valueI = tonesetValues.indexOf(value);
            if (valueI === -1) {
                setTonesetValues(old => [...old, value as string]);
            } else {
                setTonesetValues(old => old.filter(item => item !== value));
            }
        }
    };

    return (
        <div>
            <H4>
                <FormattedMessage {...messages.articlePitchTitle} />
            </H4>
            <P2>
                <FormattedMessage {...messages.articlePitchDescription} />
            </P2>
            <div>
                <SelectCheckbox
                    lightWithValue
                    label={formatMessage(messages.tonesetSolfa)}
                    name="pitch.solfaNames"
                    value={tonesetValues}
                    onChange={change}
                    options={solfaNameOptions(locale, solfaKam).map(({ value, label }) => ({
                        value,
                        label: lengthenSolfa(label, locale, solfaShort)
                    }))}
                    join=" | "
                    simulation
                />
            </div>
        </div>
    );
}

const Pitch2 = () => {
    const { formatMessage, locale } = useIntl();
    const [intervalValues, setIntervalValues] = useState(["T1", "N2", "k3", "T4"]);
    const [rangeInterval, setRangeInterval] = useState("T5");
    const changeIntervals = (e: { target: { value: string | undefined}}) => {
        let value = e.target.value;
        if (value === undefined) {
            setIntervalValues([]);
        } else {
            let valueI = intervalValues.indexOf(value);
            if (valueI === -1) {
                setIntervalValues(old => [...old, value as string]);
            } else {
                setIntervalValues(old => old.filter(item => item !== value));
            }
        }
    };
    const changeRangeInterval = (e: ISelectEvent) => {
        setRangeInterval(e.target.value);
    };
    return (
        <div>
            <Select
                lightWithValue
                label={formatMessage(messages.rangeInterval)}
                name="pitch.range.interval"
                value={rangeInterval}
                onChange={changeRangeInterval}
                options={intervalOptions(locale)}
                simulation
            />
            <SelectCheckbox
                lightWithValue
                label={formatMessage(messages.intervals)}
                name="pitch.intervals"
                value={intervalValues}
                onChange={changeIntervals}
                options={intervalOptions(locale)}
                join=", "
                simulation
            />
        </div>
    );
}

const Rhythm = () => (
    <div>
        <H4>
            <FormattedMessage {...messages.articleRhythmTitle} />
        </H4>
        <P2>
            <FormattedMessage {...messages.articleRhythmDescription} />
        </P2>
    </div>
);

const Rhythm2 = () => {
    const { formatMessage } = useIntl();
    const [rhythmPatterns, setRhythmPatterns] = useState(["ta ta", "titi"]);
    const [upbeat, setUpbeat] = useState<undefined | boolean>(undefined);
    const changeRhythmPatterns = (e: { target: { value: string | undefined }}) => {
        let value = e.target.value;
        if (value === undefined) {
            setRhythmPatterns([]);
        } else {
            let valueI = rhythmPatterns.indexOf(value);
            if (valueI === -1) {
                setRhythmPatterns(old => [...old, value as string]);
            } else {
                setRhythmPatterns(old => old.filter(item => item !== value));
            }
        }
    };
    const changeUpbeat = () => {
        switch (upbeat) {
            case true:
                setUpbeat(false);
                break;
            case false:
                setUpbeat(undefined);
                break;
            default:
                setUpbeat(true);
                break;
        }
    };
    return (
        <div>
            <hr />
            <CheckboxThreeWay label={formatMessage(messages.isUpbeat)} checked={upbeat} onChange={changeUpbeat} />
            <SelectCheckbox
                lightWithValue
                label={formatMessage(messages.rhythmPatterns)}
                name="rhythm.patterns"
                value={rhythmPatterns}
                onChange={changeRhythmPatterns}
                options={makePatternOptions(formatMessage)}
                join=", "
                simulation
            />
        </div>
    );
}
const FiltersArticle = () => {
    const { formatMessage } = useIntl();
    return (
        <StyledSpring withShadow marginTop={50}>
            <StyledArticle>
                <Seo
                    title={formatMessage(messages.articleTitle)}
                    type={"article"}
                    description={formatMessage(messages.articleDescription)}
                />
                <H2>
                    <FormattedMessage {...messages.articleTitle} />
                </H2>
                <P1 center>
                    <FormattedHTMLMessage {...messages.articleDescription} />
                </P1>
                <Flex>
                    <Desktop>
                        <Column>
                            <Pitch />
                            <Rhythm2 />
                        </Column>
                        <Column>
                            <Pitch2 />
                            <Rhythm />
                        </Column>
                    </Desktop>
                    <Mobile>
                        <Pitch />
                        <Pitch2 />
                        <Rhythm />
                        <Rhythm2 />
                    </Mobile>
                </Flex>
            </StyledArticle>
        </StyledSpring>
    );
};

export default FiltersArticle;
